import { UserViewModel } from '../../features/user/settings/user.view-model';
import { CurrentUserViewModel } from '../../core/models/current-user.view-model';
import { AbstractViewModel } from '../../core/interfaces/abstract-view-model';
import { PostApiModel } from '../../api/models/post-api.model';
import { UserApiModel } from '../../api/models/user.api-model';
import { ProjectViewModel } from '../../features/user/garage/project.view-model';
import { MediaObjectApiModel } from '../../api/models/media-object.api-model';
import { MediaObjectInterface } from '../../core/interfaces/media-object.interface';
import { EventApiModel } from '../../api/models/event.api.model';
import { ReactionApiModel } from "../../api/models/reaction.api.model";
import { ProjectApiModel } from "../../api/models/project.api-model";
import { ProjectCollaborationViewModel } from '../../features/user/collaboration/project-collaboration.view-model';
import { ProjectCollaborationApiModel } from '../../api/models/collaboration/project-collaboration.api-model';
import { ShareCardApiModel } from '../../api/models/share-card.api-model';
import { ShareRefApiModel } from '../../api/models/share-ref.api-model';

export class PostViewModel extends AbstractViewModel<PostApiModel> {


  public constructor(public apiModel: PostApiModel = new PostApiModel()) {
    super(apiModel);
    this.isPlaceholder = !apiModel.id;
    this.fromApiModel(apiModel);
  }
  postedBy: UserViewModel | CurrentUserViewModel;
  content: string;
  project: ProjectViewModel;
  event: EventApiModel;
  collaboration: ProjectCollaborationViewModel;
  createHistoryEntryOnApproval?: boolean;
  startDate?: Date;
  endDate?: Date;
  historyEntryContent?: string;
  historyPost?: string | PostApiModel;
  comments: string[];
  commentsCount = 0;
  reactions: ReactionApiModel[];
  reactionsCount = 0;
  sharesCount = 0;
  reactionsPreview: ReactionApiModel[] = [];
  media: MediaObjectApiModel[];
  type: string;
  updatedAt?: Date;
  createdAt?: Date;
  featuredAt?: Date;
  processingStatus?: 'pending' | 'processing' | 'partially_completed' | 'completed' | 'failed';
  processingUpdatedAt?: Date;
  shareType?: string | null;
  shareId?: string | null;
  shareRef?: ShareRefApiModel | null;
  shareCard?: ShareCardApiModel | null;
  localThumbnailUri?: string | null;
  localPreviewUri?: string | null;
  localPreviewType?: 'image' | 'video' | null;
  localPreviewMode?: 'none' | 'prefer-processing-video';
  isPlaceholder = true;
  currentUserReaction: ReactionApiModel | null = null;

  public static create(content: string, type: string, media: MediaObjectInterface[], projectVM: ProjectViewModel, eventAM: EventApiModel): PostViewModel {
    const postVM = new PostViewModel();
    postVM.content = content;
    postVM.media = media as MediaObjectApiModel[];
    postVM.type = type;
    if (projectVM) {
      postVM.project = projectVM;
    }
    if (eventAM) {
      postVM.event = eventAM;
    }
    return postVM;
  }

  public static update(content: string, media: MediaObjectInterface[], postVM: PostViewModel): PostViewModel {
    postVM.content = content;
    postVM.media = media as MediaObjectApiModel[];
    return postVM;
  }

  public static ensureInstance(post: PostViewModel | PostApiModel | null | undefined): PostViewModel | null {
    if (!post) {
      return null;
    }

    if (post instanceof PostViewModel) {
      return post;
    }

    const candidate = post as unknown as PostViewModel & { apiModel?: PostApiModel };
    Object.setPrototypeOf(candidate, PostViewModel.prototype);

    if (!candidate.apiModel) {
      candidate.apiModel = Object.assign(new PostApiModel(), candidate);
    }

    return candidate;
  }

  public getShareSourcePost(maxDepth = 5): PostViewModel {
    let current: PostViewModel = this;
    let depth = 0;

    while (depth < maxDepth) {
      const nextApiModel = current.shareType === 'post'
        && current.shareCard?.available
        && current.shareCard?.type === 'post'
        && current.shareCard?.post
        ? current.shareCard.post as PostApiModel
        : null;

      if (!nextApiModel) {
        return current;
      }

      const nextPost = new PostViewModel(nextApiModel);
      if (!nextPost.id || nextPost.id === current.id) {
        return current;
      }

      current = nextPost;
      depth++;
    }

    return current;
  }

  fromApiModel(apiModel: PostApiModel): this {
    this['@id'] = apiModel['@id'];
    this.id = apiModel.id;
    this.postedBy = new UserViewModel(apiModel.postedBy as UserApiModel);
    this.content = apiModel.content;
    this.event = apiModel.event as EventApiModel;
    this.comments = (apiModel.comments || []) as string[];
    this.commentsCount = apiModel.commentsCount ?? this.comments.length;
    this.reactionsPreview = ((apiModel.reactionsPreview || apiModel.reactions || []) as ReactionApiModel[]);
    this.reactions = this.reactionsPreview;
    this.reactionsCount = apiModel.reactionsCount ?? this.reactions.length;
    this.sharesCount = apiModel.sharesCount ?? 0;
    this.media = (apiModel.media || []) as MediaObjectApiModel[];
    this.type = apiModel.type;
    this.createdAt = apiModel.createdAt;
    this.updatedAt = apiModel.updatedAt;
    this.featuredAt = apiModel.featuredAt;
    this.processingStatus = apiModel.processingStatus;
    this.processingUpdatedAt = apiModel.processingUpdatedAt;
    this.shareType = apiModel.shareType ?? null;
    this.shareId = apiModel.shareId ?? null;
    this.shareRef = apiModel.shareRef ?? null;
    this.shareCard = apiModel.shareCard ?? null;
    this.currentUserReaction = apiModel.currentUserReaction ?? null;
    this.createHistoryEntryOnApproval = !!apiModel.createHistoryEntryOnApproval;
    this.startDate = apiModel.startDate;
    this.endDate = apiModel.endDate;
    this.historyEntryContent = apiModel.historyEntryContent;
    this.historyPost = apiModel.historyPost;

    if (apiModel.project) {
      this.project = new ProjectViewModel(apiModel.project as ProjectApiModel);
    }
    if (apiModel.collaboration) {
      this.collaboration = typeof apiModel.collaboration === 'string'
        ? (() => {
            const vm = new ProjectCollaborationViewModel();
            vm['@id'] = apiModel.collaboration as string;
            vm.id = Number((apiModel.collaboration as string).split('/').pop()) || 0;
            return vm;
          })()
        : new ProjectCollaborationViewModel(apiModel.collaboration as ProjectCollaborationApiModel);
    }
    return this;
  }

  getApiModel(): PostApiModel {
    return this.apiModel;
  }

  toApiModel(): PostApiModel {
    const model = new PostApiModel();
    Object.assign(model, this.apiModel);

    model.content = this.content;
    model.type = this.type;
    model.featuredAt = this.featuredAt;
    model.shareType = this.shareType ?? null;
    model.shareId = this.shareId ?? null;
    if (this.project) {
      model.project = this.project['@id'];
    }
    if (this.event) {
      model.event = this.event['@id'];
    }
    if (this.collaboration) {
      model.collaboration = this.collaboration['@id'];
    }
    model.createHistoryEntryOnApproval = !!this.createHistoryEntryOnApproval;
    model.startDate = this.startDate;
    model.endDate = this.endDate;
    model.historyEntryContent = this.historyEntryContent;
    model.media = (this.media || []).filter(e => !!e).map(e => e['@id']);

    // Sync state back to internal apiModel to ensure consistency for reconstruction
    this.apiModel.content = this.content;
    this.apiModel.type = this.type;
    this.apiModel.featuredAt = this.featuredAt;
    this.apiModel.shareType = this.shareType ?? null;
    this.apiModel.shareId = this.shareId ?? null;
    this.apiModel.createHistoryEntryOnApproval = !!this.createHistoryEntryOnApproval;
    this.apiModel.startDate = this.startDate;
    this.apiModel.endDate = this.endDate;
    this.apiModel.historyEntryContent = this.historyEntryContent;
    return model;
  }
}
